import { authApi, mainApi } from '../index';
import type {
    Invoice,
    LoginSession,
    StoreSettingsData,
    SubscriptionDetails,
    TaxDocumentsResponse,
    TaxDocumentType,
    TaxSettingsResponse,
    TaxSettingsUpdatePayload,
    UserOtherSettingsUpdate,
    UserPreference,
} from './types';
import type { ProfileFormData } from '@/features/settings/validations/profile';
import type { PasswordFormData } from '@/features/settings/validations/password';
import type { AddressFormData } from '@/features/email-flows/validations/address';
import type { AnalyticsFormData } from '@/features/settings/validations/externalAnalytics';

/**
 * Update user profile
 */
export async function updateProfile(data: ProfileFormData): Promise<void> {
    await mainApi.post('v1/users/profile-update', data);
}

/**
 * Update user password
 */
export async function updatePassword(data: PasswordFormData): Promise<void> {
    await mainApi.post('v1/users/password-update', data);
}

/**
 * Update user address
 */
export async function updateAddress(data: AddressFormData): Promise<void> {
    await mainApi.post('v1/users/address-update', data);
}

/**
 * Update user analytics settings
 */
export async function updateAnalytics(data: AnalyticsFormData): Promise<void> {
    await mainApi.post('v1/users/external-analytics-update', data);
}

/**
 * Update user other settings
 */
export async function updateOtherSettings(data: UserOtherSettingsUpdate): Promise<void> {
    await mainApi.post('v1/users/site-setting', data);
}

/**
 * Get user invoices
 */
export async function getUserInvoices(): Promise<Invoice[]> {
    const response = await mainApi.get('v1/users/invoices');
    return response.data;
}

/**
 * Get platform subscription details
 */
export async function getSubscriptionDetails(): Promise<SubscriptionDetails> {
    const response = await mainApi.get('v1/users/subscriptions-platform');
    return response.data;
}

/**
 * Update store settings
 */
export async function updateStoreSettings(data: Partial<StoreSettingsData>): Promise<void> {
    await mainApi.post('v1/users/site-setting', data);
}

/**
 * Update user preferences
 */
export async function updateUserPreferences(preferences: UserPreference[]): Promise<void> {
    await mainApi.post('v1/users/preferences', { preferences });
}

/**
 * Get user login sessions
 */
export async function getLoginSessions(): Promise<LoginSession[]> {
    const response = await authApi.get('v1/session/list');
    return response.data;
}

/**
 * Logout a specific session
 */
export async function logoutSession(sessionId: string): Promise<void> {
    await authApi.post('v1/session/logout', { id: sessionId });
}

/**
 * Log out of all sessions
 */
export async function logoutAllSessions(): Promise<void> {
    await authApi.post('v1/sessions/logout');
}

/**
 * Request account deletion
 */
export async function requestAccountDeletion(): Promise<{ request_id?: string; reasons?: string[] }> {
    const response = await mainApi.post('v1/users/request_deletion');
    return response.data;
}

/**
 * Get available tax documents (1099s, expense reports)
 */
export async function getTaxDocuments(): Promise<TaxDocumentsResponse> {
    const response = await mainApi.get('v1/tax/reports');
    return response.data;
}

/**
 * Download a specific tax document
 */
export async function downloadTaxDocument(year: string, type: TaxDocumentType): Promise<Blob> {
    const response = await mainApi.get(`v1/tax/reports/${year}/${type}`, {
        responseType: 'blob',
    });
    return response.data;
}

/**
 * Get tax settings
 */
export async function getTaxSettings(): Promise<TaxSettingsResponse> {
    const response = await mainApi.get('v1/integrations/stripe/tax/settings');
    return response.data;
}

/**
 * Update tax settings
 */
export async function updateTaxSettings(payload: TaxSettingsUpdatePayload): Promise<void> {
    await mainApi.post('v1/integrations/stripe/tax/settings', payload);
}
